
    // IMPORTS
    import { computed, defineComponent, inject, PropType } from 'vue'

    import { Card, CardHeader, CardTitle, CardBody } from '@progress/kendo-vue-layout'
    import BomIcon from '@/components/common/bom-icon.vue'
    import DocIcon from '@/components/common/doc-icon.vue'

    import { MaterialListItem } from '@/types/material'
    import { BomTreeNodeType } from '@/types/bom'
    import { buildLocalization, buildNumberFormatter, buildUnitLocalization, StringUtil } from '@/util'

    interface IMaterialAlternative {
        alternativeText: string,
        materials: MaterialListItem[]
    }

    export default defineComponent({
        name: 'materialLinkSection',

        components: {
            Card,
            CardHeader,
            CardTitle,
            CardBody,
            BomIcon,
            DocIcon
        },

        props: {
            title: { type: String as PropType<string>, required: true },
            avatar: { type: String as PropType<string>, required: true },
            materials: { type: Array as PropType<MaterialListItem[]>, required: true },
            showMaterialCount: { type: Boolean as PropType<boolean>, required: false, default: () => true },
            comment: { type: String as PropType<string>, required: false, default: () => ''} 
        },

        /********************************************************************************************************************
         PUBLIC PROPERTIES
         ********************************************************************************************************************/
        setup(props) {
            const { localizeUnit3 } = buildUnitLocalization()

            /********************************************************************************************************************
             COMPUTED PROPERTIES
             ********************************************************************************************************************/
            const numberFormatter = buildNumberFormatter()

            const nonAlternativeMaterials = computed(() => props.materials
                .filter(m => m.nodeType != BomTreeNodeType.AlternativeBomNode)
            )

            const alternatives = computed(() => props.materials
                .filter(m => m.nodeType == BomTreeNodeType.AlternativeBomNode)
                .map(m => ({
                    alternativeText: m.description,
                    materials: m.childItems ?? []
                }) as IMaterialAlternative)
            )

            const computedData = {
                numberFormatter,
                nonAlternativeMaterials,
                alternatives
            }

            /********************************************************************************************************************
             PUBLIC METHODS AND EVENT HANDLERS
             ********************************************************************************************************************/
            const { localize } = buildLocalization()

            const onClick = inject('onClick')
            const materialText = inject('materialText')

            function materialSalesText(item: MaterialListItem | null): string | null {
                const salesText = item?.salesText?.trim()

                return StringUtil.hasContent(salesText)
                    ? salesText
                    : null
            }

            function materialCountText(item: MaterialListItem): string | null {
                if (item.qty && props.showMaterialCount) {
                    return `(${numberFormatter(item.qty)} ${localizeUnit3(item.priceQtyUnit)})`
                }

                return null
            }

            const functions = { localize, onClick, materialText, materialSalesText, materialCountText }

            /********************************************************************************************************************
             PRIVATE METHODS
             ********************************************************************************************************************/

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return {
                ...computedData,
                ...functions
            }
        }
    });
